import {CloseOutlined, DeleteOutlined, EditOutlined} from '@ant-design/icons'
import {Button, Input, Modal, Switch, Table, Tooltip} from 'antd'
import Divider from 'antd/lib/divider'
import * as React from 'react'
import {useEffect, useState} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {DealBean, ItemResponseDealBeanObject, ListPagination, ListResponseDealBean} from 'src/api/api'
import {withActionsMediaQuery} from 'src/util/Util'
import dealApi from './DealApi'
import { DEALS_PAGE_SIZE } from 'src/util/Util'
import './style.css'

const Column = Table.Column

interface Props {
    areActionsVertical?: boolean
}

const DealList: React.FC<Props> = ({ areActionsVertical }) => {
    const [data, setData] = useState<DealBean[]>([])
    const [isDealId, setIsDealId] = useState(false);

    const [pagination, setPagination] = useState<{
        defaultCurrent: number,
        defaultPageSize: number,
        current: number
    }>({
        defaultCurrent: 1,
        defaultPageSize: DEALS_PAGE_SIZE,
        current: 1
    })
    const [searchQuery, setSearchQuery] = useState('')
    const [isFiltered, setIsFiltered] = useState(false)

    const location = useLocation()

    useEffect(() => {
        const pageFromQuery = Number(new URLSearchParams(location.search).get('page'))
        const pageFromLocation = !Number.isNaN(pageFromQuery) && pageFromQuery > 0 ? pageFromQuery : undefined
        const fallbackPage = 1
        const page = pageFromLocation || fallbackPage

        loadData(page)
    }, [location.search,isDealId])

    const loadData = (page: number) => {
        dealApi
            .list(pagination.defaultPageSize, page, searchQuery,isDealId)
            .then((listResponseDealBean: ListResponseDealBean) => {

                const array = listResponseDealBean.data as Array<ItemResponseDealBeanObject>
                const dealBeans: DealBean[] = array.map(item => item.data as DealBean)

                if (dealBeans.length == 0 && page > 1) {
                    loadData(page - 1)
                } else {
                    const newPagination: ListPagination = listResponseDealBean.pagination as ListPagination

                    setData(dealBeans)
                    setPagination(p => {
                        return { ...(p), total: newPagination.total, current: newPagination.currentPage ?? 1 }
                    })
                }
            })
    }

    const remove = (record: DealBean) => {
        Modal.confirm({
            title: `Delete ${record.dealId}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: () => {
                dealApi.delete(record.id as number).then(() => {
                    let page = pagination.current
                    if (data.length == 1 && pagination.current > 1) {
                        page -= 1
                    }
                    loadData(page)
                    success();
                }).catch((err)=>{
                    Modal.error({
                        title: err.error == "deal.deal.delete-not-allowed" ? "Delete deal not allowed" : err.error || err.message || "Something went wrong"
                    })
                })
            }
        })
    }
    const success = () => {
        Modal.success({
          content: 'Deleted Successfully',
        });
      };
    useEffect(() => {
        if (!searchQuery) {
            if (isFiltered) {
                loadData(1)
                setIsFiltered(false)
            }
            return
        }

        const timeoutId = window.setTimeout(() => {
            loadData(1)
            setIsFiltered(true)
        }, 1000)

        return () => window.clearTimeout(timeoutId)
    }, [isFiltered, searchQuery])

    return (
        <div>
            <div className='flex gap-[10px]'>
                <div className="c-l-search-buttons">
                    <Input
                        addonAfter={<CloseOutlined onClick={() => setSearchQuery('')} />}
                        placeholder="Deals search"
                        value={searchQuery}
                        onChange={e => {
                            setSearchQuery(e.target.value)
                        }}
                    />
                </div>
                <div className='flex gap-[10px]' >
                    <p className='text-gray'>Search by deal id</p>
<Switch checked={isDealId} onChange={(checked) => setIsDealId(checked)} />
                </div>
            </div>
            <Table
                rowKey={'id'}
                size={'middle'}
                pagination={{
                    ...pagination,
                    onChange: (page: number) => loadData(page)
                }}
                dataSource={data}>
                <Column key="id" title="ID" dataIndex="id" />
                <Column key="adUnitId" title="Ad Unit ID" dataIndex="adUnitId" />
                <Column key="dealId" title="Deal ID" dataIndex="dealId" />
                <Column key="publisherName" title="Publisher Names" dataIndex="publisherNames" render={(names: string[]) => {
                    if (!names || names.length === 0) return '-'
                    const displayText = names.slice(0, 2).join(', ')
                    const fullText = names.join(', ')
                    if (names.length > 2) {
                        return (
                            <Tooltip title={fullText}>
                                <span>{displayText}, +{names.length - 2} more</span>
                            </Tooltip>
                        )
                    }
                    return <span>{fullText}</span>
                }} />
                <Column key="dealName" title="Deal Name" dataIndex="name" />
                <Column key="dealType" title="Deal Type" dataIndex="dealType" />
                <Column key="dealStatus" title="Deal Status" dataIndex="dealStatus" />
                <Column align={'center'} key="action" title="Actions" render={(text: string, record: DealBean) => (
                    <span>
                        <Link to={{
                            pathname: `/deal/${record.id}/edit`,
                            search: `?page=${pagination.current ?? 1}`
                        }}>
                            <Button htmlType="button" title="Edit">
                                <EditOutlined />
                            </Button>
                        </Link>
                        <Divider className="actions-horisontable-divider"
                            type={areActionsVertical ? 'horizontal' : 'vertical'} />
                        <Button
                            onClick={() => remove(record)}
                            type="primary"
                            htmlType="button"
                            title="Delete"
                            danger>
                            <DeleteOutlined />
                        </Button>
                    </span>
                )} />
            </Table>
            <Link to={`/deal/create`}>
                <Button htmlType="button" type="primary">Create new</Button>
            </Link>
        </div>
    )
}

export default withActionsMediaQuery('550px')(DealList)
